// JOBE-0044.6: library UI preferences — `typeRail` (flat / tree) and
// `density` (comfortable / compact). v1 persistence in `localStorage`
// per the parent task brief; a future graph `ui-pref` entity is the
// eventual home (R12 open item; not in scope here).
//
// `density` does not change layout in v1 — JOBE-0044.7 owns the
// styling pass. This subtask threads the value through to the page
// root as `data-density="…"` so the styling pass can hook on it
// without re-plumbing.

import { useCallback, useEffect, useState } from 'react';

const KEY_TYPE_RAIL = 'library.typeRail';
const KEY_DENSITY = 'library.density';

const VALID_TYPE_RAIL = new Set(['flat', 'tree']);
const VALID_DENSITY = new Set(['comfortable', 'compact']);

const DEFAULT_TYPE_RAIL = 'flat';
const DEFAULT_DENSITY = 'comfortable';

function readLocal(key, valid, fallback) {
  try {
    const raw = globalThis.localStorage?.getItem(key);
    if (raw && valid.has(raw)) return raw;
  } catch {
    // localStorage access can throw (private-mode Safari, sandboxed
    // iframes). Fall through to the default.
  }
  return fallback;
}

function writeLocal(key, value) {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    // Silently fail; the in-memory state still updates so the UI
    // tracks the preference for the session.
  }
}

export function useLibraryPreferences() {
  const [typeRail, setTypeRailState] = useState(() =>
    readLocal(KEY_TYPE_RAIL, VALID_TYPE_RAIL, DEFAULT_TYPE_RAIL),
  );
  const [density, setDensityState] = useState(() =>
    readLocal(KEY_DENSITY, VALID_DENSITY, DEFAULT_DENSITY),
  );

  const setTypeRail = useCallback((next) => {
    if (!VALID_TYPE_RAIL.has(next)) return;
    setTypeRailState(next);
    writeLocal(KEY_TYPE_RAIL, next);
  }, []);

  const setDensity = useCallback((next) => {
    if (!VALID_DENSITY.has(next)) return;
    setDensityState(next);
    writeLocal(KEY_DENSITY, next);
  }, []);

  // Thread `density` through to the page root via a data-attribute on
  // <html> (or <body>) so the styling pass in JOBE-0044.7 can hook on
  // it without re-plumbing through props. Idempotent: a no-op when the
  // attribute already carries the right value.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const el = document.documentElement;
    const prev = el.getAttribute('data-density');
    el.setAttribute('data-density', density);
    return () => {
      // Restore the previous value on unmount so leaving the route
      // doesn't leak the attribute onto other pages.
      if (prev === null) el.removeAttribute('data-density');
      else el.setAttribute('data-density', prev);
    };
  }, [density]);

  return { typeRail, setTypeRail, density, setDensity };
}
